import { PaginatedDataStore } from '../paginated-data.store';
import { IObservableArray, observable, runInAction } from 'mobx';

export interface PaginationInterface<T> {
    current_page: number;
    data: T[];
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
}

export interface GetFilterValuesResponse {
    symbols: SocialTradingSymbolInterface[];
    channels: SocialTradingChannelInterface[];
    authors: SocialTradingAuthorInterface[];
}

export interface SocialTradingSymbolInterface {
    symbol: string;
    symbol_name?: string;
}

export interface SocialTradingAuthorInterface {
    id: number;
    name: string;
}

export interface SocialTradingChannelInterface {
    name: string;
    entry_id: number;
    entry: 'group' | 'user' | 'webinar';
    uid: string; // uid generated on frontend, to avoid id collisions
}

export abstract class AbstractSocialTradingStore<ItemInterface, FilterInterface> {
    static createUid(entry: string, id: number) {
        return `${entry}:${id}`;
    }

    readonly items: PaginatedDataStore<ItemInterface, FilterInterface>;
    readonly symbols: IObservableArray<SocialTradingSymbolInterface> = observable.array();
    readonly channels: IObservableArray<SocialTradingChannelInterface> = observable.array();
    readonly authors: IObservableArray<SocialTradingAuthorInterface> = observable.array();

    constructor() {
        this.items = this.createPaginatedDataStore();
    }

    /**
     * returns new instance of paginated data store
     */
    abstract createPaginatedDataStore(): PaginatedDataStore<ItemInterface, FilterInterface>;

    abstract fetchFilterOptions(): Promise<GetFilterValuesResponse>;

    /**
     * Fetch values for filters.
     * Fill symbols / channels / authors arrays
     */
    async loadFilterOptions() {
        const options = await this.fetchFilterOptions();

        runInAction(() => {
            this.symbols.replace(options.symbols);
            this.authors.replace(options.authors);
            this.channels.replace(
                options.channels.map((channel) => ({
                    ...channel,
                    uid: AbstractSocialTradingStore.createUid(channel.entry, channel.entry_id),
                }))
            );
        });
    }
}
